'use client'

import { useTranslations } from 'next-intl'
import { memo } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'

import { useUploadUrl } from '@/entities/storage'
import { Profile } from '@/entities/user'
import { ImageUpload } from '@/shared/lib'
import { Form, Icon } from '@/shared/ui'

type Props = {
  disabled?: boolean
}

export const ProfileImageField = ({ disabled = false }: Props) => {
  const t = useTranslations('account')

  const { generateUploadUrl } = useUploadUrl()

  const { control, setValue } = useFormContext()

  const profileImageUrl = useWatch({
    control,
    name: 'profileImageUrl',
  })

  const handleImageUpload = async ({ file }: { file: File }) => {
    const url = await generateUploadUrl(file, {
      fileName: file.name,
      fileSize: file.size,
      prefix: 'profile',
    })

    if (!url) return

    setValue('profileImageUrl', url)
  }

  return (
    <Form.Field name="profileImageUrl" disabled={disabled}>
      <Form.Label className="font-medium text-black">
        {t('profile_image_label')}
        <span className="typo-caption1 text-gray-500">
          {t('profile_image_label_opt')}
        </span>
      </Form.Label>
      <ImageUpload className="h-22 w-22" onChange={handleImageUpload}>
        <ProfileImage src={profileImageUrl} />
      </ImageUpload>
    </Form.Field>
  )
}

const ProfileImage = memo(({ src }: { src?: string }) => {
  return (
    <>
      <Profile src={src} alt="profile" size={88} />
      <div className="absolute right-0 bottom-0 flex h-6.5 w-6.5 items-center justify-center rounded-full border-2 border-white bg-black">
        <Icon name="camera" size={18} className="text-white" />
      </div>
    </>
  )
})

ProfileImage.displayName = 'ProfileImage'
